import React, { useState, useEffect } from 'react';
import { Beaker, ArrowLeftRight } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ConversionState {
  amount: string;
  substance: string;
  fromUnit: 'g' | 'ml';
  toUnit: 'g' | 'ml';
  result: number | null;
}

// Density values in g/ml for common substances
const substanceDensities: Record<string, number> = {
  'water': 1.0,
  'milk': 1.03,
  'oil': 0.92,
  'honey': 1.42,
  'flour': 0.53,
  'sugar': 0.85,
  'salt': 1.2,
  'rice': 0.78,
  'coffee': 0.56,
  'yogurt': 1.05,
  'cream': 0.98,
  'butter': 0.96,
  'vinegar': 1.01,
  'alcohol': 0.79,
  'gasoline': 0.75,
  'mercury': 13.6,
  'blood': 1.06,
  'chocolate': 1.33,
  'maple-syrup': 1.37,
  'corn-syrup': 1.38
};

const substanceNames: Record<string, string> = {
  'water': 'Voda',
  'milk': 'Mlijeko',
  'oil': 'Ulje',
  'honey': 'Med',
  'flour': 'Brašno',
  'sugar': 'Šećer',
  'salt': 'Sol',
  'rice': 'Riža',
  'coffee': 'Kava',
  'yogurt': 'Jogurt',
  'cream': 'Vrhnje',
  'butter': 'Maslac',
  'vinegar': 'Ocat',
  'alcohol': 'Alkohol',
  'gasoline': 'Benzin',
  'mercury': 'Živa',
  'blood': 'Krv',
  'chocolate': 'Čokolada',
  'maple-syrup': 'Javorov sirup',
  'corn-syrup': 'Kukuruzni sirup'
};

function GramToMlCalculator() {
  const [values, setValues] = useState<ConversionState>({
    amount: '',
    substance: 'water',
    fromUnit: 'g',
    toUnit: 'ml',
    result: null
  });

  const calculateConversion = () => {
    const amount = parseFloat(values.amount);
    
    if (isNaN(amount)) {
      return null;
    }

    const density = substanceDensities[values.substance];
    
    if (values.fromUnit === 'g' && values.toUnit === 'ml') {
      // Convert from grams to milliliters
      return amount / density;
    } else if (values.fromUnit === 'ml' && values.toUnit === 'g') {
      // Convert from milliliters to grams
      return amount * density;
    } else {
      // Same unit, no conversion needed
      return amount;
    }
  };

  const handleInputChange = (field: keyof ConversionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const swapUnits = () => {
    setValues(prev => ({
      ...prev,
      fromUnit: prev.toUnit,
      toUnit: prev.fromUnit,
      result: null
    }));
  };

  useEffect(() => {
    if (values.amount) {
      const result = calculateConversion();
      setValues(prev => ({
        ...prev,
        result: result !== null ? parseFloat(result.toFixed(3)) : null
      }));
    }
  }, [values.amount, values.substance, values.fromUnit, values.toUnit]);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(num);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Gram u ML Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online kalkulator za pretvaranje grama u mililitre i obrnuto. Podržava različite tvari s različitim gustoćama.",
    "url": "https://online-kalkulator.com/gram-u-ml",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Pretvaranje grama u mililitre",
      "Pretvaranje mililitara u grame",
      "Podrška za različite tvari",
      "Automatsko ažuriranje rezultata",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Pretvori Gram u Mililitar | Kalkulator G u mL"
        description="Besplatni online kalkulator za pretvaranje grama u mililitre i obrnuto. Podržava različite tvari s različitim gustoćama."
        canonicalUrl="/gram-u-ml"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Beaker className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Gram u ML Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Substance Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tvar
                </label>
                <select
                  value={values.substance}
                  onChange={(e) => handleInputChange('substance', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(substanceNames).map(([value, name]) => (
                    <option key={value} value={value}>{name}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Gustoća: {substanceDensities[values.substance]} g/ml
                </p>
              </div>

              {/* Conversion Fields */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iz
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      value={values.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite količinu"
                    />
                    <select
                      value={values.fromUnit}
                      onChange={(e) => handleInputChange('fromUnit', e.target.value as 'g' | 'ml')}
                      className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    >
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={swapUnits}
                  className="mt-6 p-2 text-gray-500 hover:text-blue-600 transition"
                >
                  <ArrowLeftRight className="w-6 h-6" />
                </button>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    U
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={values.result !== null ? formatNumber(values.result) : ''}
                      readOnly
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-l-lg"
                      placeholder="Rezultat"
                    />
                    <select
                      value={values.toUnit}
                      onChange={(e) => handleInputChange('toUnit', e.target.value as 'g' | 'ml')}
                      className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    >
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Formula Display */}
              {values.result !== null && (
                <div className="border-t pt-4">
                  <div className="text-center">
                    <div className="text-gray-600 mb-2">
                      {values.amount} {values.fromUnit} {substanceNames[values.substance].toLowerCase()} = {formatNumber(values.result)} {values.toUnit}
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      {values.fromUnit === 'g' && values.toUnit === 'ml' ? (
                        <span>Formula: ml = g ÷ gustoća</span>
                      ) : (
                        <span>Formula: g = ml × gustoća</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Gram u ML Pretvarač</h2>
                <p className="text-gray-600 leading-relaxed">
                  Gram u ML kalkulator je koristan alat koji vam omogućuje jednostavno pretvaranje između
                  mase (grama) i volumena (mililitara) za različite tvari. Pretvorba između grama i mililitara
                  ovisi o gustoći tvari, koja se razlikuje od jedne tvari do druge.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi Pretvorba</h2>
                <p className="text-gray-600 mb-4">
                  Pretvorba između grama i mililitara temelji se na gustoći tvari. Gustoća je definirana kao
                  masa po jedinici volumena i obično se izražava u gramima po mililitru (g/ml).
                </p>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Pretvorba iz grama u mililitre:</h3>
                    <p className="text-gray-600">
                      Volumen (ml) = Masa (g) ÷ Gustoća (g/ml)
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Pretvorba iz mililitara u grame:</h3>
                    <p className="text-gray-600">
                      Masa (g) = Volumen (ml) × Gustoća (g/ml)
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Gustoće Čestih Tvari</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tvar
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gustoća (g/ml)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(substanceNames).map(([key, name]) => (
                        <tr key={key}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {substanceDensities[key]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite tvar iz padajućeg izbornika</li>
                  <li>Unesite količinu koju želite pretvoriti</li>
                  <li>Odaberite početnu jedinicu (g ili ml)</li>
                  <li>Odaberite ciljanu jedinicu (g ili ml)</li>
                  <li>Rezultat će se automatski izračunati</li>
                  <li>Koristite gumb za zamjenu jedinica za brzu promjenu smjera pretvorbe</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Kuhanje i pečenje - pretvaranje između težine i volumena sastojaka</li>
                  <li>Laboratorijski eksperimenti - precizno mjerenje kemikalija</li>
                  <li>Farmacija - priprema lijekova i otopina</li>
                  <li>Industrija - izračun količina za proizvodnju</li>
                  <li>Svakodnevna upotreba - pretvaranje recepata iz različitih mjernih sustava</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Gustoće tvari mogu varirati ovisno o temperaturi i tlaku</li>
                  <li>Vrijednosti gustoće u kalkulatoru su približne i mogu se razlikovati od stvarnih vrijednosti</li>
                  <li>Za najpreciznije rezultate, koristite laboratorijski izmjerene gustoće</li>
                  <li>Pretvorbe su točnije za tekućine i homogene tvari</li>
                  <li>Za nehomogene tvari (poput brašna), gustoća može varirati ovisno o načinu pakiranja</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Česta Pitanja</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Koliko je 100 grama vode u mililitrima?</h3>
                    <p className="text-gray-600">
                      100 grama vode jednako je 100 mililitara, jer je gustoća vode 1 g/ml.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Zašto se gram i mililitar razlikuju za neke tvari?</h3>
                    <p className="text-gray-600">
                      Gram je jedinica mase, a mililitar je jedinica volumena. Njihov odnos ovisi o gustoći tvari.
                      Za vodu su jednaki (1g = 1ml), ali za druge tvari mogu biti različiti.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Kako pretvoriti grame brašna u mililitre?</h3>
                    <p className="text-gray-600">
                      Podijelite broj grama s gustoćom brašna (oko 0.53 g/ml). Na primjer, 100 g brašna ≈ 188.7 ml.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Kako pretvoriti mililitre ulja u grame?</h3>
                    <p className="text-gray-600">
                      Pomnožite broj mililitara s gustoćom ulja (oko 0.92 g/ml). Na primjer, 100 ml ulja ≈ 92 g.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GramToMlCalculator;